"use client"

import * as React from "react"
import Link from "next/link"
import { ArrowLeft, MapPin, Clock, Briefcase, ArrowRight } from "lucide-react"
import { Button, Badge } from "@sarkiai/ui"
import { useTranslations } from "@/components/I18nProvider"

const isDevelopment = process.env.NODE_ENV === "development"

// Mock job data - ideally this would also be translatable or fetched from an API
const jobs = isDevelopment ? [
  {
    id: "fullstack-dev",
    title: "Développeur Full Stack",
    department: "Engineering",
    location: "Douala, Cameroun",
    type: "CDI",
    remote: "Hybride",
    description: "Nous recherchons un développeur Full Stack expérimenté pour rejoindre notre équipe produit et participer au développement de nos solutions IA.",
    skills: ["React", "Next.js", "TypeScript", "Node.js", "MongoDB"],
    posted: "Il y a 2 jours",
  },
  {
    id: "ai-engineer",
    title: "Ingénieur IA / Machine Learning",
    department: "AI Research",
    location: "Douala, Cameroun",
    type: "CDI",
    remote: "Remote possible",
    description: "Rejoignez notre équipe de recherche IA pour développer les prochaines générations d'agents conversationnels.",
    skills: ["Python", "PyTorch", "LLM", "NLP", "RAG"],
    posted: "Il y a 5 jours",
  },
  {
    id: "product-designer",
    title: "Product Designer UI/UX",
    department: "Design",
    location: "Douala, Cameroun",
    type: "CDI",
    remote: "Hybride",
    description: "Nous cherchons un designer passionné pour créer des expériences utilisateur exceptionnelles pour nos produits.",
    skills: ["Figma", "Design System", "User Research", "Prototyping"],
    posted: "Il y a 1 semaine",
  },
] : []

const departments = ["Tous", "Engineering", "AI Research", "Design", "Sales", "Marketing"]

export default function JobsClient() {
  const t = useTranslations("careers.jobs")

  return (
    <main className="flex min-h-screen flex-col">
      {/* Back link */}
      <div className="container mx-auto pt-8 px-4 lg:px-12">
        <Link
          href="/careers"
          className="inline-flex items-center text-sm font-bold uppercase tracking-wider text-muted-foreground hover:text-black dark:hover:text-white transition-colors group"
        >
          <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
          {t("back")}
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-6xl  uppercase tracking-tight">
              {t("title")}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {t("subtitle")}
            </p>

            {/* Filter buttons */}
            <div className="flex flex-wrap gap-2 pt-4">
              {departments.map((dept) => (
                <Button
                  key={dept}
                  variant={dept === "Tous" ? "default" : "outline"}
                  size="sm"
                  className={dept === "Tous"
                    ? "rounded-xl bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 font-bold uppercase text-xs tracking-widest px-6 transition-all"
                    : "rounded-xl border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 font-bold uppercase text-xs tracking-widest px-6 transition-all"
                  }
                >
                  {dept}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="pb-16 md:pb-24 px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="space-y-6">
            {jobs.map((job) => (
              <Link
                key={job.id}
                href={`/careers/jobs/${job.id}`}
                className="group block p-8 md:p-10 rounded-[2.5rem] border border-black/15 dark:border-white/10 hover:shadow-2xl hover:shadow-black/5 hover:border-black/20 dark:hover:border-white/20 transition-all duration-500"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge variant="outline" className="bg-black/5 dark:bg-white/5 border-transparent text-black dark:text-white font-bold uppercase text-[10px] tracking-widest px-3 py-1">
                        {job.department}
                      </Badge>
                      <Badge variant="outline" className="border-black/10 dark:border-white/10 text-muted-foreground font-bold uppercase text-[10px] tracking-widest px-3 py-1">
                        {job.type}
                      </Badge>
                      <Badge variant="outline" className="border-black/10 dark:border-white/10 text-muted-foreground font-bold uppercase text-[10px] tracking-widest px-3 py-1">
                        {job.remote}
                      </Badge>
                    </div>

                    <h2 className="text-2xl md:text-3xl  uppercase tracking-tight transition-colors">
                      {job.title}
                    </h2>

                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2 max-w-2xl">
                      {job.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-6 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {job.posted}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      {job.skills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full bg-black/5 dark:bg-white/5 text-muted-foreground"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="shrink-0">
                    <Button className="h-12 px-8 rounded-2xl bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 font-bold uppercase tracking-widest transition-all">
                      {t("apply")}
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Spontaneous Application */}
      <section className="py-16 md:py-32 bg-black/5 dark:bg-white/5 px-4 lg:px-12">
        <div className="container mx-auto text-center space-y-8">
          <div className="w-20 h-20 rounded-3xl bg-black dark:bg-white flex items-center justify-center mx-auto shadow-xl shadow-black/10">
            <Briefcase className="h-10 w-10 text-white dark:text-black" />
          </div>
          <div className="space-y-4">
            <h2 className="text-3xl md:text-5xl  uppercase tracking-tight">
              {t("spontaneous.title")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t("spontaneous.subtitle")}
            </p>
          </div>
          <Button size="default" variant="outline" className="h-12 px-8 rounded-2xl border-black/20 dark:border-white/20 text-black dark:text-white hover:bg-black dark:hover:bg-white dark:hover:text-black font-bold uppercase tracking-widest transition-all" asChild>
            <Link href="/contact">
              {t("spontaneous.button")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
